import React, { useCallback } from 'react';
import { Autocomplete, Chip, TextField } from '@mui/material';
import Popper from '@mui/material/Popper';
import { VariableSizeList } from 'react-window';
import { filterStringOptions } from '../../utils/filterUtils';
import { ChipGroupWrapper } from './DashboardFilterBarStyle';

// ── Virtualized listbox for large option sets (>100) ──

const LISTBOX_PADDING = 8;
const ITEM_SIZE = 36;

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef(function OuterElement(props, ref) {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

const ListboxComponent = React.forwardRef(function Listbox(props, ref) {
  const { children, ...other } = props;
  const items = React.Children.toArray(children);
  const itemCount = items.length;

  const getHeight = () => Math.min(8, itemCount) * ITEM_SIZE + 2 * LISTBOX_PADDING;

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          height={getHeight()}
          width="100%"
          itemSize={() => ITEM_SIZE}
          overscanCount={5}
          itemCount={itemCount}
          outerElementType={OuterElementType}
        >
          {({ index, style }) => (
            <div
              style={{
                ...style,
                top: style.top + LISTBOX_PADDING,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {items[index]}
            </div>
          )}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

/**
 * FilterChipGroup — renders one MUI Autocomplete that shows compact chips.
 *
 * Props:
 *   label        — string (e.g. "Finca", "Operador")
 *   options      — string[]
 *   value        — string[]  (selected values)
 *   onChange      — (newValues: string[]) => void
 */
// Popper that grows to fit its content but is never narrower than the input
const FitPopper = React.forwardRef(function FitPopper(props, ref) {
  return (
    <Popper
      {...props}
      ref={ref}
      style={{ ...props.style, width: 'fit-content', minWidth: props.style?.width }}
    />
  );
});

const FilterChipGroup = ({ label, options = [], value = [], onChange }) => {
  // Guard: remove null/undefined values from options and current selection
  const safeOptions = options.filter((o) => o != null);
  const safeValue = value.filter((v) => v != null);
  const isEmpty = safeOptions.length === 0;

  const useVirtualized = safeOptions.length > 100;

  const handleFilter = useCallback(
    (opts, { inputValue }) => filterStringOptions(opts, inputValue),
    []
  );

  return (
    <ChipGroupWrapper>
      <Autocomplete
        multiple
        size="small"
        options={safeOptions}
        value={safeValue}
        getOptionLabel={(opt) => (opt != null ? String(opt) : '')}
        onChange={(_, newVal) => onChange(newVal)}
        filterOptions={handleFilter}
        disableCloseOnSelect
        limitTags={2}
        disabled={isEmpty}
        noOptionsText="Sin opciones"
        ListboxComponent={useVirtualized ? ListboxComponent : undefined}
        renderTags={(tagValues, getTagProps) =>
          tagValues.map((opt, idx) => (
            <Chip
              {...getTagProps({ index: idx })}
              key={opt}
              label={opt}
              size="small"
              sx={{
                maxWidth: 120,
                borderRadius: '6px',
                fontSize: 12,
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={isEmpty ? 'Sin opciones' : label}
            label={label}
            sx={{
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: 13,
                paddingY: '2px',
              },
            }}
          />
        )}
        PopperComponent={FitPopper}
        sx={{ width: '100%' }}
      />
    </ChipGroupWrapper>
  );
};

export default React.memo(FilterChipGroup);
